import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '@app/core/initialize-services/InitializeServices';

/**
 * Analytics Service
 * Centralized service for tracking user events and behavior across the application
 */

// Check if we're in production mode
const isProduction = import.meta.env.PROD;

/**
 * Helper function to log events only in production
 * In development, it will console.log for debugging
 */
const logAnalyticsEvent = (eventName: string, params?: Record<string, any>) => {
  // Add site hostname to all events
  const enrichedParams = {
    ...params,
    site_hostname: window.location.hostname,
    site_url: window.location.href,
  };

  // TEMP: Always send for testing (REMOVE BEFORE FINAL DEPLOY!)
  logEvent(analytics, eventName, enrichedParams);
  console.log(`[Analytics - ${isProduction ? 'PROD' : 'DEV-TESTING'}] ${eventName}`, enrichedParams);

  // Original code (restore this later):
  // if (isProduction) {
  //   logEvent(analytics, eventName, enrichedParams);
  // } else {
  //   console.log(`[Analytics - Dev] ${eventName}`, enrichedParams);
  // }
};

export const AnalyticsService = {
  /**
   * Track when a user opens/views a tour
   * @param tourId - The ID of the tour
   * @param tourTitle - The title of the tour
   */
  logTourViewed: (tourId: string, tourTitle: string) => {
    logAnalyticsEvent('tour_viewed', {
      tour_id: tourId,
      tour_title: tourTitle,
      timestamp: Date.now(),
    });
  },

  /**
   * Track when a user views a specific stop
   * @param stopId - The ID of the stop
   * @param stopTitle - The title of the stop
   * @param tourId - The ID of the parent tour
   */
  logStopViewed: (stopId: string, stopTitle: string, tourId: string) => {
    logAnalyticsEvent('user_view_stop', {
      stop_id: stopId,
      stop_title: stopTitle,
      tour_id: tourId,
      timestamp: Date.now(),
    });
  },

  /**
   * Track when a user clicks an external link
   */
  logExternalLinkClicked: (linkUrl: string, linkType?: string, stopId?: string) => {
    logAnalyticsEvent('external_link_clicked', {
      link_url: linkUrl,
      link_type: linkType || 'unknown',
      stop_id: stopId,
      timestamp: Date.now(),
    });
  },

  /**
   * Track session duration when user leaves or closes the app
   */
  logSessionDuration: (durationSeconds: number) => {
    logAnalyticsEvent('session_duration', {
      duration_seconds: durationSeconds,
      duration_minutes: Math.round(durationSeconds / 60),
      timestamp: Date.now(),
    });
  },

  // ============================================
  // LAYER 2+3: TOUR & APP ANALYTICS
  // ============================================

  // --- Tour Events ---

  logTourStart: (tourId: string, totalStops: number) => {
    logAnalyticsEvent('tour_start', {
      tour_id: tourId,
      total_stops: totalStops,
    });
  },

  logTourStartPageDuration: (tourId: string | null, durationSeconds: number) => {
    logAnalyticsEvent('tour_start_page_duration', {
      tour_id: tourId,
      duration_seconds: durationSeconds,
    });
  },

  // --- Stop Events ---

  logStopView: (tourId: string | null, stopId: string, stopTags: string, stopType: string) => {
    logAnalyticsEvent('stop_view', {
      tour_id: tourId,
      stop_id: stopId,
      stop_tags: stopTags,
      stop_type: stopType,
    });
  },

  logStopClose: (tourId: string | null, stopId: string, durationSeconds: number) => {
    logAnalyticsEvent('stop_close', {
      tour_id: tourId,
      stop_id: stopId,
      duration_seconds: durationSeconds,
    });
  },

  // --- Content Engagement Events ---

  logScrollDepth: (tourId: string | null, stopId: string, stopTags: string, maxDepth: number) => {
    logAnalyticsEvent('scroll_depth', {
      tour_id: tourId,
      stop_id: stopId,
      stop_tags: stopTags,
      max_depth: maxDepth,
    });
  },

  logAudioComplete: (tourId: string | null, stopId: string, stopTags: string, percentListened: number) => {
    logAnalyticsEvent('audio_complete', {
      tour_id: tourId,
      stop_id: stopId,
      stop_tags: stopTags,
      percent_listened: percentListened,
    });
  },

  logExternalLinkTap: (tourId: string | null, stopId: string, stopTags: string, linkDomain: string) => {
    logAnalyticsEvent('external_link_tap', {
      tour_id: tourId,
      stop_id: stopId,
      stop_tags: stopTags,
      link_domain: linkDomain,
    });
  },

  logNavigationUsed: (tourId: string | null, mode: string) => {
    logAnalyticsEvent('navigation_used', {
      tour_id: tourId,
      mode: mode,
    });
  },

  logFavoriteToggle: (tourId: string | null, stopId: string, stopTags: string, action: 'add' | 'remove') => {
    logAnalyticsEvent('favorite_toggle', {
      tour_id: tourId,
      stop_id: stopId,
      stop_tags: stopTags,
      action: action,
    });
  },

  // --- Filter & Search Events ---

  logFilterShowResults: (tourId: string | null, activeTags: string) => {
    logAnalyticsEvent('filter_show_results', {
      tour_id: tourId,
      active_tags: activeTags,
    });
  },

  logSearchPerformed: (tourId: string | null, queryText: string) => {
    logAnalyticsEvent('search_performed', {
      tour_id: tourId,
      query_text: queryText,
    });
  },

  // --- Feature Events ---

  logFeatureFirstUse: (featureName: string, tourId: string | null) => {
    logAnalyticsEvent('feature_first_use', {
      feature_name: featureName,
      tour_id: tourId,
    });
  },

  logChatOpen: (tourId: string | null) => {
    logAnalyticsEvent('chat_open', {
      tour_id: tourId,
    });
  },

  logChatClose: (tourId: string | null, durationSeconds: number) => {
    logAnalyticsEvent('chat_close', {
      tour_id: tourId,
      duration_seconds: durationSeconds,
    });
  },

  logOfflineDownload: (tourId: string | null) => {
    logAnalyticsEvent('offline_download', {
      tour_id: tourId,
    });
  },

  // --- Session Events ---

  logSessionStart: (platform: string, deviceRegion: string, entryPoint: string) => {
    logAnalyticsEvent('session_start', {
      platform: platform,
      device_region: deviceRegion,
      entry_point: entryPoint,
    });
  },

  logSessionEnd: (platform: string, durationSeconds: number, tourId: string | null) => {
    logAnalyticsEvent('session_end', {
      platform: platform,
      duration_seconds: durationSeconds,
      tour_active: tourId,
    });
  },

  // ============================================
  // USER TRACKING ACROSS SESSIONS
  // ============================================

  /**
   * Set a unique user ID to track the same user across multiple sessions
   * This allows Firebase to aggregate all events for this user across sessions
   * @param userId - Unique identifier for the user (e.g., device UUID, auth ID)
   */
  setUser: (userId: string) => {
    // TEMP: Always set for testing (REMOVE BEFORE FINAL DEPLOY!)
    setUserId(analytics, userId);
    setUserProperties(analytics, { device_id: userId });
    console.log(`[Analytics - ${isProduction ? 'PROD' : 'DEV-TESTING'}] User ID set: ${userId}`);

    // Original code (restore this later):
    // if (isProduction) {
    //   setUserId(analytics, userId);
    //   setUserProperties(analytics, { device_id: userId });
    //   console.log(`[Analytics] User ID set: ${userId}`);
    // } else {
    //   console.log(`[Analytics - Dev] User ID set: ${userId}`);
    // }
  },

  /**
   * Set custom user properties that persist across sessions
   * These properties will be attached to all future events
   * @param properties - Object containing user properties
   */
  setUserProperties: (properties: Record<string, any>) => {
    if (isProduction) {
      setUserProperties(analytics, properties);
    } else {
      console.log('[Analytics - Dev] User properties set:', properties);
    }
  },

  /**
   * Helper to set common user properties
   */
  setUserLanguage: (language: string) => {
    AnalyticsService.setUserProperties({ language });
  },

  setUserRegion: (region: string) => {
    AnalyticsService.setUserProperties({ region });
  },

  setUserType: (userType: 'free' | 'premium' | 'trial') => {
    AnalyticsService.setUserProperties({ user_type: userType });
  },
};

/**
 * Session duration tracker
 * Automatically tracks how long a user stays in the app
 */
export class SessionTracker {
  private startTime: number;
  private isTracking: boolean = false;

  constructor() {
    this.startTime = Date.now();
  }

  /**
   * Start tracking the session
   */
  start() {
    if (!this.isTracking) {
      this.startTime = Date.now();
      this.isTracking = true;

      // Track session duration when user leaves the page
      window.addEventListener('beforeunload', this.handleUnload);

      // Track session duration when page visibility changes (tab switch, minimize)
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  /**
   * Stop tracking and log the session duration
   */
  stop() {
    if (this.isTracking) {
      this.logDuration();
      this.cleanup();
    }
  }

  private handleUnload = () => {
    this.logDuration();
  };

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.logDuration();
    }
  };

  private logDuration() {
    const durationMs = Date.now() - this.startTime;
    const durationSeconds = Math.floor(durationMs / 1000);

    // Only log if session is longer than 5 seconds (avoid accidental clicks)
    if (durationSeconds > 5) {
      AnalyticsService.logSessionDuration(durationSeconds);
    }
  }

  private cleanup() {
    window.removeEventListener('beforeunload', this.handleUnload);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.isTracking = false;
  }
}

// Export a singleton instance for convenience
export const sessionTracker = new SessionTracker();
